import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import axiosClient from "../../../../api-client/AxiosClient";
import "./ProcessSettings.css";

// ─── Depth accent colours (matches wireframe) ─────────────────────────────────
const DEPTH_COLORS = ["#1E3569", "#2563EB", "#60a5fa", "#93c5fd"];
const depthColor = (d) => DEPTH_COLORS[Math.min(d, DEPTH_COLORS.length - 1)];

// ─── Unique temp-id for new nodes before they get a real DB id ─────────────────
let _tempCounter = 1;
const newTempId = () => `__new_${_tempCounter++}`;

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Recursively clone the tree from API, adding UI tracking fields. */
const normalizeDateForInput = (value) => {
  if (value == null || value === "") return "";

  if (typeof value === "number" || /^[0-9]+$/.test(String(value).trim())) {
    const parsed = new Date(Number(value));
    if (Number.isNaN(parsed.getTime())) return "";
    return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, "0")}-${String(parsed.getUTCDate()).padStart(2, "0")}`;
  }

  const str = String(value).trim();

  // ISO-like string with no timezone designator (e.g. ".NET's "2026-05-01T00:00:00")
  // represents a UTC instant but JS Date would otherwise parse it as local time.
  // Extract the date portion directly to avoid any timezone shift.
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  const hasTzDesignator = /Z$|[+-]\d{2}:?\d{2}$/.test(str);
  if (dateOnlyMatch && !hasTzDesignator) {
    return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;
  }

  const parsed = new Date(str.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return "";
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, "0")}-${String(parsed.getUTCDate()).padStart(2, "0")}`;
};

const normaliseApiTree = (nodes, parentTempId = null) =>
  (nodes || []).map((n) => ({
    taskIterationId: n.taskIterationId,
    tempId: n.taskIterationId,
    name: n.name || "",
    startDate: normalizeDateForInput(n.startDate),
    endDate: normalizeDateForInput(n.endDate),
    displayOrder: n.displayOrder ?? null,
    parentTaskIterationId: n.parentTaskIterationId ?? null,
    _parentTempId: parentTempId,
    taskCount: n.taskCount || 0,
    _isNew: false,
    _isDirty: false,
    expanded: true,
    children: normaliseApiTree(n.children || [], n.taskIterationId),
  }));

/** Deep-clone a tree. */
const cloneTree = (nodes) =>
  nodes.map((n) => ({ ...n, children: cloneTree(n.children) }));

/** Find node by tempId. Returns [node, parentArray, index] or null. */
const findNode = (tempId, nodes) => {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].tempId === tempId) return [nodes[i], nodes, i];
    const found = findNode(tempId, nodes[i].children);
    if (found) return found;
  }
  return null;
};

/** Count descendants recursively. */
const countDescendants = (node) => {
  if (!node.children?.length) return 0;
  return node.children.reduce((acc, c) => acc + 1 + countDescendants(c), 0);
};

/** BFS walk, calling fn(node) for each. */
const walkBFS = (nodes, fn) => {
  const queue = [...nodes];
  while (queue.length) {
    const n = queue.shift();
    fn(n);
    if (n.children?.length) queue.push(...n.children);
  }
};

// ─── Main Component ────────────────────────────────────────────────────────────

const ProcessSettings = () => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, node: null });
  const [pendingDeletes, setPendingDeletes] = useState([]);

  // Real DB ids queued for deletion

  // ── Load from API ──────────────────────────────────────────────────────────
  const loadTree = useCallback(async () => {
    setLoading(true);
    setPendingDeletes([]);
    try {
      const res = await axiosClient.get("/api/task/iterations/tree");
      setTree(normaliseApiTree(res.data || []));
    } catch {
      toast.error("Failed to load iterations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTree(); }, [loadTree]);

  // ── Immutable updater ──────────────────────────────────────────────────────
  const updateTree = (fn) => setTree((prev) => fn(cloneTree(prev)));

  // ── Toggle expand/collapse ─────────────────────────────────────────────────
  const toggleExpand = (tempId) => {
    updateTree((draft) => {
      const found = findNode(tempId, draft);
      if (found) found[0].expanded = !found[0].expanded;
      return draft;
    });
  };

  // ── Inline field change ────────────────────────────────────────────────────
  const handleFieldChange = (tempId, field, value) => {
    updateTree((draft) => {
      const found = findNode(tempId, draft);
      if (found) {
        found[0][field] = value;
        if (!found[0]._isNew) found[0]._isDirty = true;
      }
      return draft;
    });
  };

  // ── Add top-level iteration ────────────────────────────────────────────────
  const addTopLevel = () => {
    const node = {
      taskIterationId: null,
      tempId: newTempId(),
      name: "",
      startDate: "",
      endDate: "",
      displayOrder: null,
      parentTaskIterationId: null,
      _parentTempId: null,
      taskCount: 0,
      _isNew: true,
      _isDirty: false,
      expanded: true,
      children: [],
    };
    setTree((prev) => [...prev, node]);
    setTimeout(() => {
      const el = document.querySelector(`[data-tempid="${node.tempId}"] .ps-name-input`);
      if (el) el.focus();
    }, 60);
  };

  // ── Add child iteration ────────────────────────────────────────────────────
  const addChild = (parentTempId) => {
    const node = {
      taskIterationId: null,
      tempId: newTempId(),
      name: "",
      startDate: "",
      endDate: "",
      displayOrder: null,
      parentTaskIterationId: null,
      _parentTempId: parentTempId,
      taskCount: 0,
      _isNew: true,
      _isDirty: false,
      expanded: true,
      children: [],
    };
    updateTree((draft) => {
      const found = findNode(parentTempId, draft);
      if (found) {
        if (found[0].taskIterationId) {
          node.parentTaskIterationId = found[0].taskIterationId;
        }
        found[0].children.push(node);
        found[0].expanded = true;
      }
      return draft;
    });
    setTimeout(() => {
      const el = document.querySelector(`[data-tempid="${node.tempId}"] .ps-name-input`);
      if (el) el.focus();
    }, 60);
  };

  // ── Prompt delete ──────────────────────────────────────────────────────────
  // When user clicks delete icon we stage the IDs for deletion so the Save
  // button becomes enabled immediately. If the user cancels the modal we
  // remove those staged IDs.
  const promptDelete = (node) => {
    const ids = [];
    collectDeleteIds(node, ids);
    if (ids.length > 0) {
      setPendingDeletes((prev) => Array.from(new Set([...(Array.isArray(prev) ? prev : []), ...ids])));
    } else {
      // ensure a new reference so change detection runs
      setPendingDeletes((prev) => (Array.isArray(prev) ? [...prev] : []));
    }
    setDeleteModal({ open: true, node });
  };

  const cancelDelete = () => {
    const node = deleteModal.node;
    setDeleteModal({ open: false, node: null });
    if (!node) return;
    const ids = [];
    collectDeleteIds(node, ids);
    if (ids.length > 0) {
      setPendingDeletes((prev) => (Array.isArray(prev) ? prev.filter((id) => !ids.includes(id)) : []));
    }
  };

  const collectDeleteIds = (node, ids) => {
    if (node.taskIterationId != null) ids.push(node.taskIterationId);
    node.children.forEach((child) => collectDeleteIds(child, ids));
  };

  const removeNodeByTempId = (nodes, tempId, deleteIds) => {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].tempId === tempId) {
        if (!nodes[i]._isNew) collectDeleteIds(nodes[i], deleteIds);
        nodes.splice(i, 1);
        return true;
      }
      if (removeNodeByTempId(nodes[i].children, tempId, deleteIds)) return true;
    }
    return false;
  };

  const confirmDelete = () => {
    const nodeToDelete = deleteModal.node;
    setDeleteModal({ open: false, node: null });
    if (!nodeToDelete) return;

    const idsToDelete = [];
    updateTree((draft) => {
      removeNodeByTempId(draft, nodeToDelete.tempId, idsToDelete);
      return draft;
    });

    // Merge any server-side ids to delete; always return a new array reference
    setPendingDeletes((prev) => {
      const base = Array.isArray(prev) ? prev : [];
      const merged = new Set([...base, ...idsToDelete]);
      return Array.from(merged);
    });
  };

  // ── Save Settings (DB write only on Save click) ────────────────────────────
  // eslint-disable-next-line complexity
  const saveSettings = async () => {
    setSaving(true);
    try {
      // 1. Build ordered save list and validate all tree entries first.
      const idMap = {};
      walkBFS(tree, (n) => { if (n.taskIterationId) idMap[n.tempId] = n.taskIterationId; });
      const ordered = [];
      walkBFS(tree, (n) => ordered.push(n));

      for (const node of ordered) {
        if (!(node.name || "").trim()) {
          toast.error("Iteration name is required");
          setSaving(false);
          return;
        }
        if (
          node.startDate &&
          node.endDate &&
          new Date(node.startDate) > new Date(node.endDate)
        ) {
          toast.error(
            `${node.name || "Iteration"}: End Date must be after Start Date`
          );
          setSaving(false);
          return;
        }
      }

      // 2. Delete removed iterations before create/update operations.
      const ids = [...new Set(pendingDeletes)];
      for (const id of ids) {
        try { await axiosClient.delete(`/api/task/iterations/${id}`); }
        catch { /* ignore 404 – may be cascade-deleted by parent */ }
      }
      setPendingDeletes([]);

      // 3. Create/update in tree order (parents before children).
      for (const node of ordered) {
        // Resolve parent ID: prioritize idMap lookup for newly-created parents
        let resolvedParentId = null;
        if (node._parentTempId) {
          // Child iteration: look up parent by tempId in idMap (newly-created) or use existing DB id
          resolvedParentId = idMap[node._parentTempId] ?? node.parentTaskIterationId ?? null;
          console.log(`[Child ${node.name}] _parentTempId=${node._parentTempId}, idMap lookup=${idMap[node._parentTempId]}, fallback parentTaskIterationId=${node.parentTaskIterationId}, resolved=${resolvedParentId}`);
        } else if (node.parentTaskIterationId) {
          // Parent exists and was loaded from API
          resolvedParentId = node.parentTaskIterationId;
        }

        const formatDateForApi = (d) => {
          if (!d) return null;
          const value = typeof d === "number" ? String(d) : String(d).trim();

          // Plain "YYYY-MM-DD" from <input type="date"> — build UTC midnight
          // explicitly to avoid any local-timezone parsing ambiguity.
          const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
          if (dateOnlyMatch) {
            const [, y, m, day] = dateOnlyMatch;
            const utcMs = Date.UTC(Number(y), Number(m) - 1, Number(day));
            return new Date(utcMs).toISOString();
          }

          const parsed = /^[0-9]+$/.test(value)
            ? new Date(Number(value))
            : new Date(value.replace(" ", "T"));
          if (Number.isNaN(parsed.getTime())) return null;
          return parsed.toISOString();
        };

        const payload = {
          name: node.name.trim(),
          isActive: true,
          startDate: formatDateForApi(node.startDate),
          endDate: formatDateForApi(node.endDate),
          displayOrder: node.displayOrder === null ? null : Number(node.displayOrder),
          parentTaskIterationId:
            resolvedParentId === null || resolvedParentId === ""
              ? null
              : resolvedParentId,
        };

        if (node._isNew) {
          try {
            console.log(`[POST] Creating: ${node.name}`, payload);
            const res = await axiosClient.post("/api/task/iterations", payload);
            const newId = res.data?.taskIterationId;
            if (newId) {
              idMap[node.tempId] = newId;
              console.log(`[POST Success] ${node.name}: tempId=${node.tempId} -> DB id=${newId}, idMap now has ${Object.keys(idMap).length} entries`);
            }
          } catch (e) {
            console.error("Create iteration failed", { node, payload, error: e.response?.data || e.message });
            throw e;
          }
        } else if (node._isDirty) {
          try {
            console.log(`[PUT] Updating: ${node.name}`, payload);
            await axiosClient.put(`/api/task/iterations/${node.taskIterationId}`, payload);
          } catch (e) {
            console.error("Update iteration failed", { node, payload, error: e.response?.data || e.message });
            throw e;
          }
        }
      }

      toast.success("Settings saved successfully.");
      await loadTree();
    } catch (err) {
      console.error("SaveSettings error", err.response?.status, err.response?.data, err);
      const serverMsg = err?.response?.data?.message || err?.response?.data || null;
      toast.error(serverMsg || err?.message || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Discard Changes ────────────────────────────────────────────────────────
  const discardChanges = () => {
    loadTree();
    toast.info("Changes discarded.");
  };

  // ── Has pending changes? ───────────────────────────────────────────────────
  const hasPendingChanges = useMemo(() => {
    if (pendingDeletes.length > 0) return true;
    let dirty = false;
    walkBFS(tree, (n) => { if (n._isNew || n._isDirty) dirty = true; });
    return dirty;
  }, [tree, pendingDeletes]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="ps-page">
      <div className="ps-page-title">Process Settings</div>
      <div className="ps-page-sub">
        Define project iterations and phases used across Task Management.
      </div>

      <div className="ps-tab-bar">
        <div className="ps-tab ps-tab-active">Iterations</div>
      </div>

      <div className="ps-card">
        <div className="ps-card-toolbar">
          <div>
            <div className="ps-card-toolbar-title">Iterations</div>
            <div className="ps-card-toolbar-sub">
              Add top-level iterations and nest child iterations within them.
            </div>
          </div>
          <button className="ps-btn ps-btn-primary" onClick={addTopLevel} disabled={saving}>
            + Add Iteration
          </button>
        </div>

        <div className="ps-header-row">
          <div>Name</div>
          <div>Start Date</div>
          <div>End Date</div>
          <div className="ps-col-right">Actions</div>
        </div>

        <div className="ps-tree">
          {(() => {
            if (loading) {
              return (
                <div className="ps-empty-state">
                  <div className="ps-empty-icon">⏳</div>
                  <p>Loading iterations…</p>
                </div>
              );
            }
            if (tree.length === 0) {
              return (
                <div className="ps-empty-state">
                  <div className="ps-empty-icon">📅</div>
                  <p>No iterations yet. Click <strong>+ Add Iteration</strong> to get started.</p>
                </div>
              );
            }
            return tree.map((node) => (
              <IterationNode
                key={node.tempId}
                node={node}
                depth={0}
                onToggle={toggleExpand}
                onChange={handleFieldChange}
                onAddChild={addChild}
                onDelete={promptDelete}
                saving={saving}
              />
            ));
          })()}
        </div>
      </div>

      {/* Save bar */}
      <div className="ps-save-bar">
        <span className="ps-save-hint">
          {hasPendingChanges ? "⚠️ You have unsaved changes" : ""}
        </span>
        <button
          className="ps-btn ps-btn-secondary"
          onClick={discardChanges}
          disabled={saving || loading}
        >
          Discard Changes
        </button>
        <button
          className="ps-btn ps-btn-save"
          onClick={saveSettings}
          disabled={saving || loading || !hasPendingChanges}
        >
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {/* Delete modal */}
      {deleteModal.open && deleteModal.node && (
        <DeleteModal
          node={deleteModal.node}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

// ─── IterationNode ─────────────────────────────────────────────────────────────

const IterationNode = ({ node, depth, onToggle, onChange, onAddChild, onDelete, saving }) => {
  const hasChildren = node.children?.length > 0;
  const color = depthColor(depth);

  return (
    <>
      <div
        className={[
          "ps-iter-row",
          node._isNew ? "ps-row-new" : "",
          node._isDirty ? "ps-row-dirty" : "",
        ].join(" ").trim()}
        data-tempid={node.tempId}
      >
        {/* ── Name cell ── */}
        <div className="ps-name-cell">
          {Array.from({ length: depth }).map((_, i) => (
            <span key={`indent-${depth}-${i}`} className="ps-indent">
              {i === depth - 1 && (
                <>
                  <span className="ps-connector-h" />
                  <span className="ps-connector-v" />
                </>
              )}
            </span>
          ))}

          <button
            className={`ps-expand-btn${hasChildren ? "" : " ps-expand-btn--hidden"}`}
            onClick={() => onToggle(node.tempId)}
            tabIndex={hasChildren ? 0 : -1}
            aria-label={node.expanded ? "Collapse" : "Expand"}
          >
            {node.expanded ? "▼" : "▶"}
          </button>

          <span className="ps-color-bar" style={{ background: color }} />

          <input
            className="ps-name-input"
            type="text"
            value={node.name}
            placeholder="Iteration name…"
            onChange={(e) => onChange(node.tempId, "name", e.target.value)}
            disabled={saving}
          />

          {node._isNew && <span className="ps-badge-new">New</span>}
        </div>

        {/* ── Start date ── */}
        <div>
          <input
            type="date"
            className={`ps-date-input${node.startDate ? "" : " ps-date-empty"}`}
            value={node.startDate || ""}
            onChange={(e) => {
              onChange(node.tempId, "startDate", e.target.value);
            }}
            disabled={saving}
          />
        </div>

        {/* ── End date ── */}
        <div>
          <input
            type="date"
            className={`ps-date-input${node.endDate ? "" : " ps-date-empty"}`}
            value={node.endDate || ""}
            onChange={(e) => {
              onChange(node.tempId, "endDate", e.target.value);
            }}
            disabled={saving}
          />
        </div>

        {/* ── Actions ── */}
        <div className="ps-actions-cell">
          <button
            className="ps-btn-icon ps-btn-icon--add"
            title="Add child iteration"
            onClick={() => onAddChild(node.tempId)}
            disabled={saving}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <button
            className="ps-btn-icon ps-btn-icon--delete"
            title="Delete iteration"
            onClick={() => onDelete(node)}
            disabled={saving}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Children */}
      {node.expanded && hasChildren &&
        node.children.map((child) => (
          <IterationNode
            key={child.tempId}
            node={child}
            depth={depth + 1}
            onToggle={onToggle}
            onChange={onChange}
            onAddChild={onAddChild}
            onDelete={onDelete}
            saving={saving}
          />
        ))
      }
    </>
  );
};

IterationNode.propTypes = {
  node: PropTypes.shape({
    tempId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskIterationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    displayOrder: PropTypes.number,
    parentTaskIterationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    _parentTempId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskCount: PropTypes.number,
    _isNew: PropTypes.bool.isRequired,
    _isDirty: PropTypes.bool.isRequired,
    expanded: PropTypes.bool.isRequired,
    children: PropTypes.array.isRequired,
  }).isRequired,
  depth: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onAddChild: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
};

// ─── Delete Confirmation Modal ─────────────────────────────────────────────────

const DeleteModal = ({ node, onConfirm, onCancel }) => {
  const childCount = countDescendants(node);
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="ps-modal-overlay"
      role="button"
      tabIndex={0}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}
    >
      <div className="ps-modal">
        <div className="ps-modal-header">
          <span className="ps-modal-title">Delete Iteration</span>
          <button className="ps-btn-icon" onClick={onCancel} aria-label="Close">✕</button>
        </div>
        <div className="ps-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{node.name || "this iteration"}</strong>?
          </p>
          {childCount > 0 && (
            <p className="ps-modal-warn">
              ⚠️ This will also delete {childCount} child iteration
              {childCount > 1 ? "s" : ""} inside it.
            </p>
          )}
        </div>
        <div className="ps-modal-footer">
          <button className="ps-btn ps-btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="ps-btn ps-btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
};

DeleteModal.propTypes = {
  node: PropTypes.shape({
    tempId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskIterationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    children: PropTypes.array.isRequired,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ProcessSettings;
